// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type UrlStatePatch<K extends string> = Partial<Record<K, string | null>>;

/**
 * Navigational page state held in the query string, so a link or a refresh
 * lands on the same view. The rule: a URL addresses one place in the app.
 *
 * - Values are strings; parse numbers and lists at the call site.
 * - A key at its default is left out of the URL, so the default view keeps a
 *   clean address and old links stay valid.
 * - `patch` changes several keys in one navigation — a filter change and its
 *   page reset must not be two, or the second clobbers the first.
 * - `null` in a patch returns a key to its default.
 * - Replaces the history entry by default: typing in a search box or paging
 *   must not fill the back button. Pass `{ push: true }` when the change is a
 *   new place the user would go Back from, like opening a detail view.
 * - Keys not in `defaults` are left untouched, so two components on one page
 *   can own different keys.
 */
export function useUrlState<K extends string>(
  defaults: Record<K, string>
): [Record<K, string>, (_patch: UrlStatePatch<K>, _opts?: { push?: boolean }) => void] {
  const [params, setParams] = useSearchParams();

  const keys = Object.keys(defaults) as K[];
  const signature = keys.map(k => `${k}=${params.get(k) ?? ''}`).join('&');
  const state = useMemo(
    () => Object.fromEntries(keys.map(k => [k, params.get(k) ?? defaults[k]])) as Record<K, string>,
    // `signature` captures every value read; `defaults` is a per-render literal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature]
  );

  const defaultsKey = JSON.stringify(defaults);
  const patch = useCallback(
    (changes: UrlStatePatch<K>, { push = false }: { push?: boolean } = {}) => {
      const base = JSON.parse(defaultsKey) as Record<K, string>;
      setParams(
        prev => {
          const next = new URLSearchParams(prev);
          (Object.keys(changes) as K[]).forEach(k => {
            const v = changes[k];
            if (v === null || v === undefined || v === base[k]) next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: !push }
      );
    },
    [setParams, defaultsKey]
  );

  return [state, patch];
}

/** A comma-joined list in one query key. Empty string ↔ empty list. */
export const splitList = (value: string): string[] => (value ? value.split(',') : []);
export const joinList = (values: readonly string[]): string => values.join(',');
